import { useCallback } from "react";
import { ExtractedContract, SessionBuilding, BuildingCheckResponse } from "../../types/contract";
import { normalizeName } from "../../utils/normalization";

export interface ContractConfirmationParams {
  selectedContract: ExtractedContract | null;
  finalContracts: ExtractedContract[];
  setSelectedContract: (contract: ExtractedContract | null) => void;
  setAggregatedContracts: React.Dispatch<React.SetStateAction<ExtractedContract[]>>;
  setFinalContracts: React.Dispatch<React.SetStateAction<ExtractedContract[]>>;
  sessionConfirmedData: { buildings: SessionBuilding[] };
  setSessionConfirmedData: React.Dispatch<React.SetStateAction<{ buildings: SessionBuilding[] }>>;
  confirmedContracts: Set<number>;
  setConfirmedContracts: React.Dispatch<React.SetStateAction<Set<number>>>;
  needsReviewContracts: Set<number>;
  setNeedsReviewContracts: React.Dispatch<React.SetStateAction<Set<number>>>;
  buildingChecks: Map<number, BuildingCheckResponse>;
  conflictResolutions: Map<number, { building: 'keep' | 'update' | null; owner: 'keep' | 'update' | null }>;
  selectNextItem: (
    currentId: number,
    updatedConfirmed: Set<number>,
    updatedNeedsReview: Set<number>,
    shouldScroll: boolean
  ) => void;
  setSnackbar: React.Dispatch<React.SetStateAction<{
    open: boolean;
    message: string;
    severity?: 'success' | 'info' | 'warning' | 'error';
  }>>;
  aggregatedContracts: ExtractedContract[];
  handleSelectContract: (contract: ExtractedContract | null, shouldScroll: boolean) => void;
}

export const useContractConfirmation = (params: ContractConfirmationParams) => {
  const {
    selectedContract,
    finalContracts,
    setSelectedContract,
    setAggregatedContracts,
    setFinalContracts,
    setSessionConfirmedData,
    confirmedContracts,
    setConfirmedContracts,
    needsReviewContracts,
    setNeedsReviewContracts,
    buildingChecks,
    conflictResolutions,
    selectNextItem,
    setSnackbar,
    aggregatedContracts,
    handleSelectContract,
  } = params;

  const handleConfirmAndNext = useCallback(() => {
    if (!selectedContract) return;

    // 重複チェックの選択状況を確認
    const contractIdx = finalContracts.findIndex(
      (c) =>
        c.property_name === selectedContract.property_name &&
        c.room_name === selectedContract.room_name &&
        c.lessee_name === selectedContract.lessee_name
    );

    if (contractIdx !== -1 && buildingChecks.has(contractIdx)) {
      const resolution = conflictResolutions.get(contractIdx);
      if (resolution && (resolution.building === null || resolution.owner === null)) {
        setSnackbar({
          open: true,
          message: '建物・家主情報の選択を完了してください',
          severity: 'warning' as const,
        });
        return;
      }
    }

    try {
      // sessionConfirmedData に建物・家主データを追加
      const newBuilding: SessionBuilding = {
        tempId: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: selectedContract.property_name || '',
        name_normalized: normalizeName(selectedContract.property_name),
        name_kana: selectedContract.building_name_kana?.value ?? undefined,
        building_code: selectedContract.building_code?.value ?? undefined,
        postal_code: selectedContract.property_postal_code?.value ?? undefined,
        address1: selectedContract.property_address?.value ?? undefined,
        address2: selectedContract.property_address2?.value ?? undefined,
        owner: {
          name: selectedContract.owner_name?.value ?? undefined,
          name_normalized: normalizeName(selectedContract.owner_name?.value),
          name_kana: selectedContract.owner_name_kana?.value ?? undefined,
          postal_code: selectedContract.owner_postal_code?.value ?? undefined,
          address1: selectedContract.owner_address1?.value ?? undefined,
          address2: selectedContract.owner_address2?.value ?? undefined,
          home_phone: selectedContract.owner_home_phone?.value ?? undefined,
          mobile_phone: selectedContract.owner_mobile_phone?.value ?? undefined,
          bank_1_branch_code: selectedContract.owner_bank_1_branch_code?.value ?? undefined,
          bank_1_account_type: selectedContract.owner_bank_1_account_type?.value ?? undefined,
          bank_1_account_number: selectedContract.owner_bank_1_account_number?.value ?? undefined,
          bank_1_account_holder_name: selectedContract.owner_bank_1_account_holder_name?.value ?? undefined,
          bank_2_branch_code: selectedContract.owner_bank_2_branch_code?.value ?? undefined,
          bank_2_account_type: selectedContract.owner_bank_2_account_type?.value ?? undefined,
          bank_2_account_number: selectedContract.owner_bank_2_account_number?.value ?? undefined,
          bank_2_account_holder_name: selectedContract.owner_bank_2_account_holder_name?.value ?? undefined,
        },
        sourceContractId: selectedContract.id!,
        createdAt: new Date(),
      };

      setSessionConfirmedData(prev => ({
        buildings: [...prev.buildings, newBuilding]
      }));

      // 契約データに tempId を設定
      const updatedContract = {
        ...selectedContract,
        session_building_temp_id: newBuilding.tempId
      };
      setSelectedContract(updatedContract);
      setAggregatedContracts(prev =>
        prev.map(c => c.id === selectedContract.id ? updatedContract : c)
      );
      setFinalContracts(prev =>
        prev.map(c => c.id === selectedContract.id ? updatedContract : c)
      );

      // Add to confirmed list
      const newConfirmed = new Set(confirmedContracts);
      newConfirmed.add(selectedContract.id!);
      setConfirmedContracts(newConfirmed);

      // Remove from needs review list if it exists there
      const newNeedsReview = new Set(needsReviewContracts);
      if (newNeedsReview.has(selectedContract.id!)) {
        newNeedsReview.delete(selectedContract.id!);
        setNeedsReviewContracts(newNeedsReview);
      }

      selectNextItem(selectedContract.id!, newConfirmed, newNeedsReview, true);

    } catch (error) {
      console.error('確認処理エラー:', error);
      setSnackbar({
        open: true,
        message: 'データの保存に失敗しました',
        severity: 'error' as const,
      });
    }
  }, [
    selectedContract,
    finalContracts,
    buildingChecks,
    conflictResolutions,
    setSnackbar,
    setSessionConfirmedData,
    setSelectedContract,
    setAggregatedContracts,
    setFinalContracts,
    confirmedContracts,
    setConfirmedContracts,
    needsReviewContracts,
    setNeedsReviewContracts,
    selectNextItem,
  ]);

  const handleMarkForReview = useCallback(() => {
    if (!selectedContract) return;

    // Add to needs review list
    const newNeedsReview = new Set(needsReviewContracts);
    newNeedsReview.add(selectedContract.id!);
    setNeedsReviewContracts(newNeedsReview);

    // Remove from confirmed list if it exists there
    const newConfirmed = new Set(confirmedContracts);
    if (newConfirmed.has(selectedContract.id!)) {
      newConfirmed.delete(selectedContract.id!);
      setConfirmedContracts(newConfirmed);
    }

    selectNextItem(selectedContract.id!, newConfirmed, newNeedsReview, true);
  }, [
    selectedContract,
    needsReviewContracts,
    setNeedsReviewContracts,
    confirmedContracts,
    setConfirmedContracts,
    selectNextItem,
  ]);

  const handlePrev = useCallback(() => {
    if (!selectedContract) return;
    const currentIndex = aggregatedContracts.findIndex(
      (c) => c.id === selectedContract.id
    );
    if (currentIndex > 0) {
      handleSelectContract(aggregatedContracts[currentIndex - 1], true);
    }
  }, [selectedContract, aggregatedContracts, handleSelectContract]);

  const handleNext = useCallback(() => {
    if (!selectedContract) return;
    const currentIndex = aggregatedContracts.findIndex(
      (c) => c.id === selectedContract.id
    );
    if (currentIndex < aggregatedContracts.length - 1) {
      handleSelectContract(aggregatedContracts[currentIndex + 1], true);
    }
  }, [selectedContract, aggregatedContracts, handleSelectContract]);

  return {
    handleConfirmAndNext,
    handleMarkForReview,
    handlePrev,
    handleNext,
  };
};